import React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, Check, X, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/primitives";

// One-tap upgrade: starts the Stripe Pro subscription checkout and redirects.
export function useProCheckout() {
  return useMutation({
    mutationFn: () => api("/checkout", { method: "POST", body: { kind: "pro" } }),
    onSuccess: (r) => { if (r.url) window.location.href = r.url; },
    onError: (e) => toast.error(e.message || "Could not start checkout"),
  });
}

// Full-page "preview" gate shown to free members on Pro-only pages.
export function ProPreview({ title, tagline, perks = [], testID = "pro-preview" }) {
  const nav = useNavigate();
  const checkout = useProCheckout();
  return (
    <div className="max-w-xl mx-auto py-10" data-testid={testID}>
      <div className="rounded-3xl border border-brand/30 bg-brand-tint/40 p-8 text-center relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand/10 blur-2xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-brand-2 font-medium mb-2">
            <Lock className="w-3.5 h-3.5" />Two Degrees Individual Plus
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink">{title}</h1>
          <p className="text-muted-foreground mt-2">{tagline}</p>
          {perks.length > 0 && (
            <ul className="text-left max-w-sm mx-auto mt-6 flex flex-col gap-2.5">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-ink">
                  <Check className="w-4 h-4 text-brand shrink-0 mt-0.5" />{p}
                </li>
              ))}
            </ul>
          )}
          <Button size="lg" className="mt-7" data-testid={`${testID}-upgrade`} loading={checkout.isPending} onClick={() => checkout.mutate()}>
            Upgrade to Individual Plus
          </Button>
          <button data-testid={`${testID}-see-plans`} onClick={() => nav("/pro")} className="block mx-auto mt-3 text-xs text-muted-foreground hover:text-ink underline">
            See all plans
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal shown when a free member taps a Pro-gated action (RSVP, buy ticket, pay).
export function UpgradeGateDialog({ open, onClose, title = "Individual Plus required", message }) {
  const nav = useNavigate();
  const checkout = useProCheckout();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-ink/50 flex items-end sm:items-center justify-center sm:p-6" onClick={onClose} data-testid="upgrade-gate-dialog">
      <div className="bg-surface-2 w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground" data-testid="upgrade-gate-close"><X className="w-5 h-5" /></button>
        <h2 className="font-serif text-2xl text-ink">{title}</h2>
        <p className="text-muted-foreground mt-2">{message}</p>
        <div className="flex flex-col gap-2 mt-6">
          <Button size="lg" data-testid="upgrade-gate-upgrade" loading={checkout.isPending} onClick={() => checkout.mutate()}>Upgrade to Individual Plus</Button>
          <button data-testid="upgrade-gate-see-plans" onClick={() => { onClose(); nav("/pro"); }} className="text-sm text-muted-foreground hover:text-ink">See all plans</button>
        </div>
      </div>
    </div>
  );
}

// Slim banner shown to members on an active Pro trial, counting down to expiry.
export function TrialBanner() {
  const { user } = useAuth();
  const checkout = useProCheckout();
  const [dismissed, setDismissed] = React.useState(() => sessionStorage.getItem("trial_banner_dismissed") === "1");
  if (dismissed || !user?.on_trial || !user?.trial_ends_at) return null;
  const ms = new Date(user.trial_ends_at).getTime() - Date.now();
  if (ms <= 0) return null;
  const days = Math.ceil(ms / 86400000);
  const label = days <= 1 ? "Last day" : `${days} days left`;
  return (
    <div data-testid="trial-banner" className="flex items-center gap-3 px-4 py-2.5 bg-ink text-surface rounded-lg mb-4">
      <Clock className="w-4 h-4 text-brand shrink-0" />
      <p className="text-sm flex-1 min-w-0"><span className="font-medium">{label}</span> in your Individual Plus trial, keep connections, messaging, events & more.</p>
      <button data-testid="trial-banner-upgrade" onClick={() => checkout.mutate()} disabled={checkout.isPending}
        className="shrink-0 text-sm font-medium bg-brand text-white px-3 py-1.5 rounded-pill hover:brightness-105 disabled:opacity-50">
        Keep Individual Plus
      </button>
      <button data-testid="trial-banner-dismiss" aria-label="Dismiss"
        onClick={() => { sessionStorage.setItem("trial_banner_dismissed", "1"); setDismissed(true); }}
        className="shrink-0 -mr-1 p-1 rounded-full text-surface/60 hover:text-surface hover:bg-white/10 transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
